import { ServiceSparkModel } from '../../core/api/models';

export function isCurrentStaffOnService(
  service: ServiceSparkModel,
  currentUserId: number | null | undefined
): boolean {
  if (!currentUserId) {
    return false;
  }

  if (service.StaffID === currentUserId) {
    return true;
  }

  return service.StaffIDs?.includes(currentUserId) ?? false;
}

export function toLocalDayValue(
  dateValue: string | Date | null | undefined
): number | null {
  if (!dateValue) {
    return null;
  }

  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

export function isSameLocalDay(
  left: string | Date | null | undefined,
  right: string | Date | null | undefined
): boolean {
  const leftDay = toLocalDayValue(left);
  const rightDay = toLocalDayValue(right);
  return leftDay != null && leftDay === rightDay;
}

export function getSessionLinkDate(
  session?: {
    StartedDate?: string | Date | null;
    GroupSessionCreatedDate?: string | Date | null;
  } | null,
  now: Date = new Date()
): Date {
  const linkDate = session?.StartedDate ?? session?.GroupSessionCreatedDate;
  if (linkDate) {
    const started = linkDate instanceof Date
      ? linkDate
      : new Date(linkDate);
    if (!Number.isNaN(started.getTime())) {
      return started;
    }
  }
  return now;
}

export const SAME_DAY_SERVICE_LINK_ERROR =
  'A session can only be linked to a service on the same day.';

export function canLinkSessionToService(
  session: {
    StartedDate?: string | Date | null;
    GroupSessionCreatedDate?: string | Date | null;
  } | null | undefined,
  service: {
    Date?: string | Date | null;
  } | null | undefined,
  now: Date = new Date()
): boolean {
  if (!service) {
    return false;
  }
  return isSameLocalDay(service.Date, getSessionLinkDate(session, now));
}

export function getLinkablePatientServices(
  services: ServiceSparkModel[] | null | undefined,
  currentUserId: number | null | undefined,
  options?: {
    hideLinked?: boolean;
    matchDate?: string | Date | null;
    now?: Date;
  }
): ServiceSparkModel[] {
  const hideLinked = options?.hideLinked ?? true;
  const matchDate = options?.matchDate ?? options?.now ?? new Date();

  return (services ?? [])
    .filter((service) => !!service.ID)
    .filter((service) => isCurrentStaffOnService(service, currentUserId))
    .filter((service) => isSameLocalDay(service.Date, matchDate))
    .filter((service) => !hideLinked || !service.LinkedSessionID)
    .sort((a, b) => {
      const aTime = a.StartTime
        ? new Date(a.StartTime).getTime()
        : a.Date
          ? new Date(a.Date).getTime()
          : 0;
      const bTime = b.StartTime
        ? new Date(b.StartTime).getTime()
        : b.Date
          ? new Date(b.Date).getTime()
          : 0;
      return aTime - bTime;
    });
}

export function getPossibleServiceForSession(
  session: {
    ServiceID?: number | null;
    StartedDate?: string | Date | null;
    GroupSessionCreatedDate?: string | Date | null;
    DeletedDate?: string | Date | null;
  },
  linkableServices: ServiceSparkModel[] | null | undefined,
  now: Date = new Date()
): ServiceSparkModel | null {
  if (session.ServiceID || session.DeletedDate) {
    return null;
  }

  const sessionDay = toLocalDayValue(getSessionLinkDate(session, now));
  if (sessionDay == null) {
    return null;
  }

  const matches = (linkableServices ?? []).filter(
    (service) => toLocalDayValue(service.Date) === sessionDay
  );
  if (matches.length === 0) {
    return null;
  }
  if (matches.length === 1 || !session.StartedDate) {
    return matches[0];
  }

  const sessionTime = new Date(session.StartedDate).getTime();
  return [...matches].sort((a, b) => {
    const aTime = a.StartTime ? new Date(a.StartTime).getTime() : sessionTime;
    const bTime = b.StartTime ? new Date(b.StartTime).getTime() : sessionTime;
    return Math.abs(aTime - sessionTime) - Math.abs(bTime - sessionTime);
  })[0];
}
